// API клиент для управления пользователями
import { apiClient } from './client'

// Типы
export interface UserItem {
  id: string
  email: string
  full_name: string
  role: string
  org_id: string | null
  org_code: string | null
  org_name: string | null
  org_short_name: string | null
  zitadel_id: string | null
  is_active: boolean
  last_login: string | null
  created_at: string
  updated_at: string
  invited_by_name: string | null
  invited_at: string | null
  legacy_userid: string | null
  position_title: string | null
  positions: string[]
  phone: string | null
  telegram_id: string | null
  rls_source_file: string | null
  rls_source_sheet: string | null
  rls_source_updated_at: string | null
  rls_imported_at: string | null
  login_readiness_status: string | null
  login_readiness_label: string | null
  login_expected_login_mode: string | null
  login_mode_label: string | null
  login_yandex_expected: boolean
  login_yandex_linked: boolean
}

export interface UserListResponse {
  items: UserItem[]
  total: number
  page: number
  page_size: number
}

export interface UserCreatePayload {
  email: string
  full_name: string
  role: string
  org_id?: string
  password?: string
}

export interface UserUpdatePayload {
  email?: string
  full_name?: string
  role?: string
  org_id?: string
  is_active?: boolean
}

export interface UserListParams {
  org_id?: string
  role?: string
  is_active?: boolean
  search?: string
  login_mode?: string
  login_readiness_status?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  page_size?: number
}

export interface OrganizationItem {
  id: string
  code: string
  name: string
  short_name: string | null
  kind: string
  is_active: boolean
}

// API функции
export const fetchUsers = async (params: UserListParams): Promise<UserListResponse> => {
  const response = await apiClient.get<UserListResponse>('/users/', { params })
  return response.data
}

export const fetchUser = async (userId: string): Promise<UserItem> => {
  const response = await apiClient.get<UserItem>(`/users/${userId}`)
  return response.data
}

export const createUser = async (data: UserCreatePayload): Promise<UserItem> => {
  const response = await apiClient.post<UserItem>('/users/', data)
  return response.data
}

export const updateUser = async (
  userId: string,
  data: UserUpdatePayload
): Promise<UserItem> => {
  const response = await apiClient.patch<UserItem>(`/users/${userId}`, data)
  return response.data
}

export const deactivateUser = async (userId: string): Promise<UserItem> => {
  const response = await apiClient.post<UserItem>(`/users/${userId}/deactivate`)
  return response.data
}

export const reactivateUser = async (userId: string): Promise<UserItem> => {
  const response = await apiClient.post<UserItem>(`/users/${userId}/reactivate`)
  return response.data
}

export const deleteUser = async (userId: string): Promise<void> => {
  await apiClient.delete(`/users/${userId}`)
}

export const anonymizeUser = async (userId: string): Promise<UserItem> => {
  const response = await apiClient.post<UserItem>(`/users/${userId}/anonymize`)
  return response.data
}

export const resetPassword = async (userId: string): Promise<{ detail: string }> => {
  const response = await apiClient.post<{ detail: string }>(`/users/${userId}/reset-password`)
  return response.data
}

export const fetchOrganizations = async (): Promise<OrganizationItem[]> => {
  const response = await apiClient.get<OrganizationItem[]>('/users/organizations')
  return response.data
}

// Группы доступа пользователя
export interface UserAccessGroup {
  id: string
  name: string
  zitadel_role_key: string
  description: string | null
  is_active: boolean
  org_id: string | null
  member_count: number
  created_at: string
  updated_at: string
}

export const fetchUserAccessGroups = async (userId: string): Promise<UserAccessGroup[]> => {
  const response = await apiClient.get<UserAccessGroup[]>(`/users/${userId}/access-groups`)
  return response.data
}

export interface UserDashboardAccessSourceGroup {
  id: string
  name: string
  zitadel_role_key: string
  org_id: string | null
}

export interface UserDashboardAccessItem {
  datalens_entry_id: string
  entry_type: string
  entry_title: string | null
  label: string
  source_groups: UserDashboardAccessSourceGroup[]
  source_group_count: number
  inherited_scope: string
  override_risk: string
  override_note: string
  projected_visibility: string
  projected_note: string
}

export interface UserDashboardAccessSummary {
  total_entries: number
  single_group: number
  multi_group_same_org: number
  multi_group_cross_org: number
  override_risk_low: number
  override_risk_medium: number
  override_risk_high: number
}

export interface UserDashboardAccessExceptionItem {
  id: string
  exception_type: string
  datalens_entry_id: string
  entry_type: string
  entry_title: string | null
  label: string
  note: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  overlaps_inherited: boolean
  conflicts_with_other_exception: boolean
  projected_effect: string
  projected_note: string
}

export interface UserDashboardAccessExceptionSummary {
  total_exceptions: number
  direct_grants: number
  direct_denies: number
  overlapping_exceptions: number
  conflicting_entries: number
}

export interface UserDashboardAccessProjectionSummary {
  current_visible_entries: number
  projected_visible_entries: number
  would_add_visible: number
  would_remove_visible: number
  redundant_grants: number
  redundant_denies: number
  conflicting_entries: number
}

export interface UserDashboardAccessExceptionCreatePayload {
  datalens_entry_id: string
  entry_type?: string
  entry_title?: string
  note?: string
}

export interface UserDashboardAccessResponse {
  user_id: string
  has_wildcard: boolean
  wildcard_reason: string | null
  total: number
  items: UserDashboardAccessItem[]
  summary: UserDashboardAccessSummary
  exceptions: UserDashboardAccessExceptionItem[]
  exception_summary: UserDashboardAccessExceptionSummary
  projection_summary: UserDashboardAccessProjectionSummary
}

export const fetchUserDashboardAccess = async (userId: string): Promise<UserDashboardAccessResponse> => {
  const response = await apiClient.get<UserDashboardAccessResponse>(`/users/${userId}/dashboard-access`)
  return response.data
}

export const createUserDashboardGrant = async (
  userId: string,
  data: UserDashboardAccessExceptionCreatePayload
): Promise<UserDashboardAccessExceptionItem> => {
  const response = await apiClient.post<UserDashboardAccessExceptionItem>(
    `/users/${userId}/dashboard-access/grants`,
    data
  )
  return response.data
}

export const deleteUserDashboardGrant = async (
  userId: string,
  exceptionId: string
): Promise<void> => {
  await apiClient.delete(`/users/${userId}/dashboard-access/grants/${exceptionId}`)
}

export const createUserDashboardDeny = async (
  userId: string,
  data: UserDashboardAccessExceptionCreatePayload
): Promise<UserDashboardAccessExceptionItem> => {
  const response = await apiClient.post<UserDashboardAccessExceptionItem>(
    `/users/${userId}/dashboard-access/denies`,
    data
  )
  return response.data
}

export const deleteUserDashboardDeny = async (
  userId: string,
  exceptionId: string
): Promise<void> => {
  await apiClient.delete(`/users/${userId}/dashboard-access/denies/${exceptionId}`)
}
